import {
  groupArrayBy,
  findFromArray,
  truncateString,
  objectHasProperty,
  getBarChartOptions,
  getPieChartOptions,
  capitalizeFirstLetter,
} from 'utils/';
import Highcharts from 'highcharts';
import React, { useMemo } from 'react';
import { useMobileView } from 'hooks/';
import { white } from 'constants/color';
import {
  StyledContainer,
  StyledTileWrapper,
  StyledGraphWrapper,
  StyledScrollWrapper,
} from 'components/Item/List/List.styled';
import { Bar, Tile } from 'components/Widgets/';
import Item from 'containers/Inventory/Item/Item';
import { REORDER_LEVEL } from 'constants/general';
import { FlexCol, FlexRow } from 'components/Layout/';
import { Collapsible } from 'components/Collapsible/';
import HighchartsReact from 'highcharts-react-official';
import { Record, StockMoveGraph } from 'components/Item/';
import { object, array, arrayOf, bool, func, shape } from 'prop-types';

/**
 * @param {{
 * good: {}
 * tags : []
 * items : []
 * conditions : []
 * categories : []
 * warehouses : []
 * stockCondition; {},
 * isLoading : boolean
 * showAddUI : boolean
 * onArchive : Function
 * upload : {
 *  data : [],
 *  isLoading : boolean
 * }
 * stockMove : {
 *  data : [],
 *  isLoading : boolean
 * }
 * isOnBoarding : boolean
 * setShowAddUI : Function
 * onLoadStockMoveGraph : Function
 * }} param
 */
function List({
  good,
  tags,
  items,
  upload,
  showAddUI,
  stockMove,
  isLoading,
  onArchive,
  categories,
  warehouses,
  conditions,
  setShowAddUI,
  isOnBoarding,
  stockCondition,
  onLoadStockMoveGraph,
}) {
  const isMobileView = useMobileView();
  const itemDetails = useMemo(() => {
    return items?.map(function (item) {
      let itemTag;
      const { item_tags: itemTags } = item;
      if (Array.isArray(itemTags)) {
        itemTag = itemTags[0];
      }
      return {
        ...item,
        tags: findFromArray(tags, 'id', itemTag),
        itemState: findFromArray(conditions, 'id', item.item_state),
        category: findFromArray(categories, 'id', item.item_category),
        warehouse: findFromArray(warehouses, 'id', item.warehouse_id),
      };
    });
  }, [tags, items, categories, conditions, warehouses]);

  const groupedWarehouses = useMemo(() => {
    return groupArrayBy(itemDetails, 'warehouse_id');
  }, [itemDetails]);

  const totalWarehouseItems = useMemo(() => {
    if (objectHasProperty(groupedWarehouses)) {
      let total = 0;
      const data = [];
      for (const [, value] of Object.entries(groupedWarehouses)) {
        const filtered = items.filter(function ({ warehouse_id: id }) {
          return id === value.warehouse_id;
        });
        const quantity = filtered.reduce(function (
          previousValue,
          currentValue,
        ) {
          return previousValue + parseFloat(currentValue.quantity);
        },
        0);
        total += quantity;
        data.push({
          y: quantity,
          name: truncateString(value?.warehouse?.name, 50, true),
        });
      }
      return {
        data,
        total,
      };
    } else {
      return {
        data: [],
        total: 0,
      };
    }
  }, [groupedWarehouses]);

  const totalItemsOptions = useMemo(() => {
    return getPieChartOptions(
      totalWarehouseItems.data,
      '', // caption
      '', // legend
      totalWarehouseItems.total,
      {
        // subTitleStyles
        y: totalWarehouseItems.data.length * -8,
      },
    );
  }, [totalWarehouseItems]);

  const restock = groupArrayBy(
    useMemo(() => {
      return [...items].filter(function ({ quantity }) {
        return parseFloat(quantity) < REORDER_LEVEL;
      });
    }, [items]),
    'item_name',
  );

  const restockInfo = useMemo(() => {
    const labels = [];
    for (const [, value] of Object.entries(restock)) {
      const data = {
        name: value.item_name,
        quantity: parseFloat(value.quantity),
      };
      labels.push(data);
    }

    return labels;
  }, [restock]);

  const itemByCondition = useMemo(() => {
    const labels = [];
    const series = [];
    Object.entries(stockCondition || {}).forEach(function ([, entry]) {
      const { type = '', count = 0 } = entry || {};
      series.push(count);
      labels.push(capitalizeFirstLetter(type));
    });
    return { labels, series };
  }, [stockCondition]);

  const itemByConditionOptions = useMemo(() => {
    const { labels, series } = itemByCondition;
    return getBarChartOptions(
      series,
      'Items Grouped By Condition',
      'Item Count',
      labels,
      true,
      '',
      'Item Category',
      'Total Item Count',
    );
  }, [itemByCondition]);

  return (
    <StyledContainer>
      {isOnBoarding ? null : (
        <StyledTileWrapper>
          <FlexRow>
            <FlexCol flexWidth={4}>
              <Tile caption="Total Items">
                <StyledScrollWrapper>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={totalItemsOptions}
                    containerProps={{ style: { height: '100%' } }}
                  />
                </StyledScrollWrapper>
              </Tile>
            </FlexCol>
            <FlexCol flexWidth={4}>
              <Tile caption="To be Restocked">
                <StyledScrollWrapper>
                  {restockInfo.map(function (item) {
                    const { name } = item;
                    return <Bar key={name} data={item} />;
                  })}
                </StyledScrollWrapper>
              </Tile>
            </FlexCol>
            <FlexCol flexWidth={4}>
              <Tile caption="Items by Condition">
                <StyledScrollWrapper>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={itemByConditionOptions}
                    containerProps={{ style: { height: '100%' } }}
                  />
                </StyledScrollWrapper>
              </Tile>
            </FlexCol>
          </FlexRow>
        </StyledTileWrapper>
      )}
      {showAddUI && isOnBoarding ? (
        <Item isOnBoarding setShowAddUI={setShowAddUI} />
      ) : (
        <Record
          good={good}
          upload={upload}
          items={itemDetails}
          isLoading={isLoading}
          onArchive={onArchive}
          isMobileView={isMobileView}
          isOnBoarding={isOnBoarding}
        />
      )}
      {isOnBoarding ? null : (
        <StyledGraphWrapper>
          <Collapsible
            open={isMobileView}
            backGroundColor={white}
            caption="Stock Move Graph"
            minHeight={isMobileView ? 750 : 500}
          >
            <StockMoveGraph
              items={items}
              stockMove={stockMove}
              warehouses={warehouses}
              onLoadStockMoveGraph={onLoadStockMoveGraph}
            />
          </Collapsible>
        </StyledGraphWrapper>
      )}
    </StyledContainer>
  );
}

List.propTypes = {
  tags: array,
  items: array,
  isLoading: bool,
  showAddUI: func,
  onArchive: func,
  categories: array,
  conditions: array,
  warehouses: array,
  setShowAddUI: bool,
  isOnBoarding: bool,
  stockMove: shape({
    isLoading: bool,
    data: arrayOf(object),
  }),
  upload: shape({
    isLoading: bool,
    data: arrayOf(object),
  }),
  onLoadStockMoveGraph: func,
};

export default List;
